import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { useNavigate, useLocation } from 'react-router-dom';
import './ManageProjectsByEmployees.css';
import { Col, Row, Nav, Spinner, Alert } from 'react-bootstrap';
import TodaysWorkStatOfAllProjects from './TodaysWorkStatOfAllProjects';
import MonthsWorkStatOfAllProjects from './MonthsWorkStatOfAllProjects';
import OvertimeWorkStatOfAllProjects from './OvertimeWorkStatOfAllProjects';
import TotalWorkStatOfAllProjects from './TotalWorkStatOfAllProjects';
import axiosInstance from '../../utils/axiosInstance';
import { useTranslation } from 'react-i18next';
import Table from '../Table/Table';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { formatISOToLocalTime, normalizeDbTimestampToUtcIso } from '../../utils/timeUtils';
import format from 'date-fns/format';
import WorkDataDateRangeFilter from '../common/WorkDataDateRangeFilter';
import {
    getLocalCalendarMonthBounds,
    resolveWorkFetchDateRange,
    sumDurationSeconds,
    secondsToDurationString,
} from '../../utils/workDataDateRange';

const ManageProjectsByEmployees = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { employeeId, employeeName } = location.state || {};

    const [activeTab, setActiveTab] = useState('projects');

    const [listRangeStart, setListRangeStart] = useState('');
    const [listRangeEnd, setListRangeEnd] = useState('');
    const listApiRange = useMemo(() => {
        if (!listRangeStart && !listRangeEnd) return null;
        return resolveWorkFetchDateRange(listRangeStart, listRangeEnd);
    }, [listRangeStart, listRangeEnd]);

    const [tsRangeStart, setTsRangeStart] = useState(() => getLocalCalendarMonthBounds().startDate);
    const [tsRangeEnd, setTsRangeEnd] = useState(() => getLocalCalendarMonthBounds().endDate);
    const timesheetQueryParams = useMemo(
        () => resolveWorkFetchDateRange(tsRangeStart, tsRangeEnd),
        [tsRangeStart, tsRangeEnd]
    );

    const [totalDuration, setTotalDuration] = useState('00:00:00');
    const [workDuration, setWorkDuration] = useState('00:00:00');
    const [totalWorkData, setTotalWorkData] = useState({ startDate: '', endDate: '', totalDuration: '00:00:00' });
    const [totalOvertimeData, setTotalOvertimeData] = useState({ startDate: '', endDate: '', totalOvertimeDuration: '00:00:00' });
    const [projectWorkData, setProjectWorkData] = useState([]);
    const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

    const [tsTotalDuration, setTsTotalDuration] = useState('00:00:00');
    const [tsWorkDuration, setTsWorkDuration] = useState('00:00:00');
    const [tsTotalWorkData, setTsTotalWorkData] = useState({ startDate: '', endDate: '', totalDuration: '00:00:00' });
    const [tsOvertimeData, setTsOvertimeData] = useState({ startDate: '', endDate: '', totalOvertimeDuration: '00:00:00' });
    const [timesheetRows, setTimesheetRows] = useState([]);
    const [timesheetLoading, setTimesheetLoading] = useState(false);

    const showListReset = !!(listRangeStart || listRangeEnd);
    const tsMonthNow = getLocalCalendarMonthBounds();
    const showTsReset =
        tsRangeStart !== tsMonthNow.startDate || tsRangeEnd !== tsMonthNow.endDate;

    useEffect(() => {
        const detectTimezone = () => {
            const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (detectedZone !== userTimeZone) {
                setUserTimeZone(detectedZone);
            }
        };

        window.addEventListener('focus', detectTimezone);
        return () => {
            window.removeEventListener('focus', detectTimezone);
        };
    }, [userTimeZone]);

    const onBack = () => {
        navigate('/EmployeeOperations');
    };

    const onTotalWorkData = (data) => {
        setTotalWorkData(data);
    };

    const onTotalOvertimeData = (info) => {
        setTotalOvertimeData(info);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';

            const options = {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                timeZone: userTimeZone
            };
            return date.toLocaleDateString(undefined, options);
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const headers = [
        t("projectDetails_projectName"),
        t("projectDetails_todayWork"),
        t("projectDetails_totalWorking"),
        t("projectDetails_totalOvertime"),
        t("attendance_login"),
    ];

    const timesheetTableHeaders = [
        t("employeeWorkDetails_SrNo"),
        t("employeeWorkDetails_date"),
        t("employeeWorkDetails_startTime"),
        t("employeeWorkDetails_EndTime"),
        t("employeeWorkDetails_duration"),
        t("employeeWorkDetails_comment"),
    ];

    const handleEmployeeClick = async (row) => {
        const projectName = row.id.toString();
        try {
            const { projectId } = await fetchProjectDetails(projectName);
            navigate(`/employee-project-details/${employeeId}`, {
                state: {
                    employeeId,
                    employeeName,
                    projectId,
                    projectName
                },
            });
        } catch (error) {
            console.error("Error handling employee click:", error);
        }
    };

    const rangeParamsOpts = (range) => ({ params: { startDate: range.startDate, endDate: range.endDate } });

    const fetchProjectListData = useCallback(async () => {
        if (!employeeId) return;
        const p = listApiRange ? rangeParamsOpts(listApiRange) : {};
        try {
            const [wd, mw, tw, ot, det] = await Promise.all([
                axiosInstance.get(`/admin/ManageProjectsByEmployees-work-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-monthly-work-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-total-work-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-overtime-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-employee-work-details/${employeeId}`, p),
            ]);

            setTotalDuration(wd.data.totalDuration);
            setWorkDuration(mw.data.totalWorkDuration);
            const { startDate, endDate, totalDuration: totDur } = tw.data;
            onTotalWorkData({
                startDate: startDate ?? '',
                endDate: endDate ?? '',
                totalDuration: totDur
            });
            const { startDate: os, endDate: oe, totalOvertimeDuration } = ot.data;
            onTotalOvertimeData({
                startDate: os ?? '',
                endDate: oe ?? '',
                totalOvertimeDuration
            });

            const normalizedData = det.data.map(project => ({
                ...project,
                currentlyWorking: project.currentlyWorking ?
                    formatISOToLocalTime(
                        normalizeDbTimestampToUtcIso(
                            project.currentlyWorking,
                            new Date().toISOString().split('T')[0]
                        )
                    ) :
                    '-'
            }));
            setProjectWorkData(normalizedData);
        } catch (error) {
            console.error('Error fetching project list data:', error.message);
        }
    }, [employeeId, listApiRange]);

    const fetchTimesheetAggregates = useCallback(async () => {
        if (!employeeId) return;
        const p = rangeParamsOpts(timesheetQueryParams);
        try {
            const [wd, mw, tw, ot] = await Promise.all([
                axiosInstance.get(`/admin/ManageProjectsByEmployees-work-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-monthly-work-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-total-work-duration/${employeeId}`, p),
                axiosInstance.get(`/admin/ManageProjectsByEmployees-overtime-duration/${employeeId}`, p),
            ]);
            setTsTotalDuration(wd.data.totalDuration);
            setTsWorkDuration(mw.data.totalWorkDuration);
            const { startDate, endDate, totalDuration: totDur } = tw.data;
            setTsTotalWorkData({
                startDate: startDate ?? '',
                endDate: endDate ?? '',
                totalDuration: totDur
            });
            const { startDate: os, endDate: oe, totalOvertimeDuration } = ot.data;
            setTsOvertimeData({
                startDate: os ?? '',
                endDate: oe ?? '',
                totalOvertimeDuration
            });
        } catch (error) {
            console.error('Error fetching timesheet aggregates:', error.message);
        }
    }, [employeeId, timesheetQueryParams]);

    const fetchTimesheetRows = useCallback(async () => {
        if (!employeeId) return;
        setTimesheetLoading(true);
        try {
            const response = await axiosInstance.get(
                `/admin/ManageProjectsByEmployees-timesheet/${employeeId}`,
                rangeParamsOpts(timesheetQueryParams)
            );
            const normalized = response.data.map((row) => ({
                ...row,
                time_start: normalizeDbTimestampToUtcIso(row.time_start, row.work_date),
                time_stop: normalizeDbTimestampToUtcIso(row.time_stop, row.work_date),
            }));
            setTimesheetRows(normalized);
        } catch (error) {
            console.error('Error fetching timesheet rows:', error.message);
            setTimesheetRows([]);
        } finally {
            setTimesheetLoading(false);
        }
    }, [employeeId, timesheetQueryParams]);

    useEffect(() => {
        fetchProjectListData();
    }, [fetchProjectListData]);

    useEffect(() => {
        if (activeTab !== 'timesheet') return;
        fetchTimesheetAggregates();
        fetchTimesheetRows();
    }, [activeTab, fetchTimesheetAggregates, fetchTimesheetRows]);

    const fetchProjectDetails = async (projectName) => {
        try {
            const response = await axiosInstance.get('/admin/ManageProjectsByEmployees-projectDetails', {
                params: { projectName },
            });
            return { projectId: response.data.projectId };
        } catch (error) {
            console.error("Error fetching project details and assignId:", error);
            alert("Failed to fetch project details. Please try again.");
            throw error;
        }
    };

    const downloadExcel = () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(`${employeeName}'s Projects Data`);

        sheet.views = [{ showGridLines: false }];
        sheet.addRow([]);

        sheet.mergeCells('B1:F1');
        sheet.getCell('B1').value = `${employeeName}'s Projects Data`;
        sheet.getCell('B1').font = { size: 18, bold: true };
        sheet.getCell('B1').alignment = { vertical: 'middle', horizontal: 'center' };
        sheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };

        sheet.addRow(['']);
        sheet.addRow(['', 'Todays Work', 'Months Work', 'Total Work', 'Total Overtime', '']);
        sheet.getRow(3).font = { bold: true };
        sheet.getRow(3).alignment = { vertical: 'middle', horizontal: 'center' };

        sheet.addRow([
            '',
            totalDuration,
            workDuration,
            totalWorkData.totalDuration,
            totalOvertimeData.totalOvertimeDuration,
            '',
        ]);

        const currentDate = new Date().toLocaleDateString();
        const currentMonthYear = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

        sheet.addRow([
            '',
            formatDate(currentDate),
            currentMonthYear,
            `${formatDate(totalWorkData.startDate)} to ${formatDate(totalWorkData.endDate)}`,
            `${formatDate(totalOvertimeData.startDate)} to ${formatDate(totalOvertimeData.endDate)}`,
            '',
        ]);

        sheet.addRow(['']);
        sheet.addRow(['', ...headers]);
        sheet.getRow(7).eachCell((cell, colNumber) => {
            if (colNumber !== 1 && colNumber !== 7) {
                cell.font = { bold: true };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };
            }
        });

        projectWorkData.forEach((project) => {
            sheet.addRow([
                '',
                project.projectName || '-',
                project.todayWorkDuration || '-',
                project.totalWorkDuration || '-',
                project.totalOvertimeDuration || '-',
                project.currentlyWorking || '-',
                '',
            ]);
        });

        sheet.columns.forEach((column) => {
            column.width = 40;
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer]), `${employeeName}_Projects_Data.xlsx`);
        });
    };

    const downloadTimesheetExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Timesheet');
        sheet.views = [{ showGridLines: false }];

        const fromDate = timesheetQueryParams.startDate;
        const toDate = timesheetQueryParams.endDate;
        const projectNames = Array.from(
            new Set(
                (timesheetRows || [])
                    .map((r) => (r.project_name || '').toString().trim())
                    .filter(Boolean)
            )
        );
        const timeSerialFromIso = (iso) => {
            if (!iso) return null;
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return null;
            const h = d.getHours();
            const m = d.getMinutes();
            const s = d.getSeconds();
            return (h * 3600 + m * 60 + s) / 86400;
        };

        sheet.addRow([]);
        sheet.mergeCells('B1:I1');
        sheet.getCell('B1').value = `${employeeName} — ${t('Timesheet')}`;
        sheet.getCell('B1').font = { size: 18, bold: true };
        sheet.getCell('B1').alignment = { vertical: 'middle', horizontal: 'center' };
        sheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };

        sheet.addRow([]);
        sheet.mergeCells('B2:I2');
        const filterCell = sheet.getCell('B2');
        filterCell.value = `From:  ${fromDate}  To:  ${toDate}`;
        filterCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

        sheet.addRow([]);
        // Summary table (above timesheet)
        const summaryHdr = sheet.addRow(['', 'Project', 'Sponsor', 'Total Hours', 'Status', 'Billing Type']);
        summaryHdr.eachCell((cell, colNumber) => {
            if (colNumber > 1) {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
        });
        const summaryRows = [];
        if (projectNames.length === 0) {
            const r = sheet.addRow(['', '-', '-', '', '-', '-']);
            r.getCell(4).numFmt = '[h]:mm';
            summaryRows.push(r);
        } else {
            projectNames.forEach((p) => {
                const r = sheet.addRow(['', p, '-', '', '-', '-']);
                r.getCell(4).numFmt = '[h]:mm';
                summaryRows.push(r);
            });
        }
        sheet.addRow([]);

        // Timesheet table headers (keeps existing header labels)
        const excelTimesheetHeaders = [
            timesheetTableHeaders[0],
            'Project',
            ...timesheetTableHeaders.slice(1),
        ];
        const hdr = sheet.addRow(['', ...excelTimesheetHeaders]);
        hdr.eachCell((cell, colNumber) => {
            if (colNumber > 1) {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
        });

        const dataStartRow = sheet.rowCount + 1;
        timesheetRows.forEach((row, index) => {
            const startSerial = timeSerialFromIso(row.time_start);
            const stopSerial = timeSerialFromIso(row.time_stop);

            // Columns: A blank, B sr, C project, D date, E start, F stop, G duration, H comments
            const excelRow = sheet.addRow([
                '',
                index + 1,
                row.project_name || '-',
                format(new Date(row.work_date), 'dd/MM/yyyy'),
                startSerial,
                stopSerial,
                null,
                row.comments || '-',
            ]);

            excelRow.getCell(5).numFmt = 'hh:mm';
            excelRow.getCell(6).numFmt = 'hh:mm';

            const r = excelRow.number;
            const durationCell = excelRow.getCell(7);
            durationCell.value = { formula: `IF(OR(E${r}=\"\",F${r}=\"\"),\"\",F${r}-E${r})` };
            durationCell.numFmt = '[h]:mm';
        });
        const dataEndRow = sheet.rowCount;

        // Total row (Total Hours under Duration column)
        const totalRow = sheet.addRow(['', '', '', '', '', 'Total Hours', null, '']);
        totalRow.getCell(6).font = { bold: true };
        totalRow.getCell(6).alignment = { horizontal: 'right', vertical: 'middle' };
        totalRow.getCell(7).value = { formula: `SUM(G${dataStartRow}:G${dataEndRow})` };
        totalRow.getCell(7).numFmt = '[h]:mm';
        totalRow.getCell(7).font = { bold: true };

        // Summary Total Hours per project (SUMIF on Project column vs Duration)
        summaryRows.forEach((r) => {
            const rr = r.number;
            r.getCell(4).value = { formula: `SUMIF(C${dataStartRow}:C${dataEndRow},B${rr},G${dataStartRow}:G${dataEndRow})` };
        });

        // Column widths (wider comments)
        sheet.getColumn(1).width = 2;
        sheet.getColumn(2).width = 10;
        sheet.getColumn(3).width = 28;
        sheet.getColumn(4).width = 14;
        sheet.getColumn(5).width = 12;
        sheet.getColumn(6).width = 12;
        sheet.getColumn(7).width = 14;
        sheet.getColumn(8).width = 40;
        sheet.getColumn(9).width = 2;
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${employeeName}_Timesheet.xlsx`);
    };

    const handleDownload = () => {
        if (activeTab === 'timesheet') {
            downloadTimesheetExcel();
        } else {
            downloadExcel();
        }
    };

    return (
        <div className='ManageProjectsByEmployees-container'>
            <div className='button-section'>
                <button className="backButton" onClick={onBack}>
                    <IoIosArrowBack />Back
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <button className="downloadButton" onClick={handleDownload}>Download</button>
                </div>
            </div>
            <h2 className=''>Work Data of {employeeName}</h2>

            <Nav
                variant="tabs"
                className="ManageProjectsByEmployees-tabs mb-3 d-flex justify-content-center align-items-center"
                activeKey={activeTab}
                onSelect={(k) => k && setActiveTab(k)}
            >
                <Nav.Item>
                    <Nav.Link eventKey="projects" style={{ padding: '0.5rem 4rem' }}>Project list</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="timesheet" style={{ padding: '0.5rem 4rem' }}>Timesheet</Nav.Link>
                </Nav.Item>
            </Nav>

            {activeTab === 'projects' && (
                <>
                    <div className='d-flex justify-content-end'>
                        <WorkDataDateRangeFilter
                            startDate={listRangeStart}
                            endDate={listRangeEnd}
                            onStartDateChange={setListRangeStart}
                            onEndDateChange={setListRangeEnd}
                            showClearButton={showListReset}
                            labels={{ clear: 'Clear filter' }}
                            onClear={() => {
                                setListRangeStart('');
                                setListRangeEnd('');
                            }}
                        />
                    </div>
                    <div className='Empstats'>
                        <Row>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <TodaysWorkStatOfAllProjects totalDuration={totalDuration} />
                            </Col>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <MonthsWorkStatOfAllProjects totalDuration={workDuration} />
                            </Col>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <TotalWorkStatOfAllProjects
                                    startDate={totalWorkData.startDate}
                                    endDate={totalWorkData.endDate}
                                    totalDuration={totalWorkData.totalDuration}
                                />
                            </Col>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <OvertimeWorkStatOfAllProjects
                                    startDate={totalOvertimeData.startDate}
                                    endDate={totalOvertimeData.endDate}
                                    totalDuration={totalOvertimeData.totalOvertimeDuration}
                                />
                            </Col>
                        </Row>
                    </div>
                    <div className="ManageProjectsByEmployees-table-section">
                        <div className="EmployeeDetails-body">
                            <div className="EmployeeDetails-header">
                                <span className="EmployeeDetails-headerTitle">Projects List</span>
                                <span className="CurrentlyWorking-active">
                                    {t("projectDetails_empWorkTimeformat")}
                                </span>
                            </div>
                            <div className="EmployeeDetails-table-body">
                                <Table
                                    headers={headers}
                                    data={projectWorkData.map((project) => [
                                        project.projectName,
                                        project.todayWorkDuration,
                                        project.totalWorkDuration,
                                        project.totalOvertimeDuration,
                                        project.currentlyWorking,
                                    ])}
                                    onRowClick={(row) => handleEmployeeClick({ id: row[0], name: row[1] })}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'timesheet' && (
                <>
                    <div className='d-flex justify-content-end'>
                        <WorkDataDateRangeFilter
                            startDate={tsRangeStart}
                            endDate={tsRangeEnd}
                            onStartDateChange={setTsRangeStart}
                            onEndDateChange={setTsRangeEnd}
                            showClearButton={showTsReset}
                            labels={{ clear: 'This month' }}
                            onClear={() => {
                                const b = getLocalCalendarMonthBounds();
                                setTsRangeStart(b.startDate);
                                setTsRangeEnd(b.endDate);
                            }}
                        />
                    </div>
                    <div className='Empstats'>
                        <Row>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <TodaysWorkStatOfAllProjects totalDuration={tsTotalDuration} />
                            </Col>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <MonthsWorkStatOfAllProjects totalDuration={tsWorkDuration} />
                            </Col>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <TotalWorkStatOfAllProjects
                                    startDate={tsTotalWorkData.startDate}
                                    endDate={tsTotalWorkData.endDate}
                                    totalDuration={tsTotalWorkData.totalDuration}
                                />
                            </Col>
                            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
                                <OvertimeWorkStatOfAllProjects
                                    startDate={tsOvertimeData.startDate}
                                    endDate={tsOvertimeData.endDate}
                                    totalDuration={tsOvertimeData.totalOvertimeDuration}
                                />
                            </Col>
                        </Row>
                    </div>

                    <div className="ManageProjectsByEmployees-timesheet-legend">
                        <span className="legend-item"><span className="legend-swatch overtime-swatch" /> {t("employeeWorkDetails_overtime")}</span>
                        <span className="legend-item"><span className="legend-swatch regular-swatch" /> Regular</span>
                    </div>

                    {timesheetLoading ? (
                        <div className="d-flex align-items-center gap-2 my-4">
                            <Spinner animation="border" size="sm" />
                            <span>Loading timesheet…</span>
                        </div>
                    ) : timesheetRows.length === 0 ? (
                        <Alert variant="light" className="mt-3">No timesheet rows for this range.</Alert>
                    ) : (
                        <div className="ManageProjectsByEmployees-timesheet-table-wrap">
                            <table className="table table-bordered table-sm" style={{ marginTop: '16px', minWidth: '720px' }}>
                                <thead>
                                    <tr>
                                        {timesheetTableHeaders.map((h) => (
                                            <th key={h}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {timesheetRows.map((row, index) => (
                                        <tr
                                            key={`${row.work_date}-${index}`}
                                            className={row.overtime === 'Overtime' ? 'mpbe-timesheet-row--ot' : 'mpbe-timesheet-row--reg'}
                                        >
                                            <td>{index + 1}</td>
                                            <td>{format(new Date(row.work_date), 'dd/MM/yyyy')}</td>
                                            <td>{formatISOToLocalTime(row.time_start)}</td>
                                            <td>{row.time_stop ? formatISOToLocalTime(row.time_stop) : '-'}</td>
                                            <td>{row.work_duration || '-'}</td>
                                            <td>
                                                <span title={row.comments}>
                                                    {row.comments && row.comments.length > 40
                                                        ? `${row.comments.slice(0, 40)}…`
                                                        : row.comments || '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ManageProjectsByEmployees;
